/**
 * DNS-record helpers for the connect-domain sub-flow.
 *
 * The records come from the session domain-connect config (Entri), whose
 * `dnsRecords` is either a flat `DnsRecord[]` or a keyed
 * `Record<string, DnsRecord[]>` (conditional sets). We flatten either shape to a
 * single display list and format the Data / TTL columns the way the Figma shows
 * them (priority hints inline, TTL as "1 Hour" / "600 seconds").
 */

import type { EntriConfig } from 'entrijs';

/** One DNS row as rendered in the connect-domain table / cards. */
export interface DnsRecordView {
  /** Record type, e.g. "A" / "NS" / "CNAME" / "TXT" / "MX" / "SOA". */
  type: string;
  /** Host / Name column ("@", "*", "www", "email", …). */
  host: string;
  /** Data / Value column (with any priority / pointer hint folded in). */
  data: string;
  /** TTL in seconds (raw), formatted for display by {@link formatTtl}. */
  ttl: number;
}

/** Entri's per-record shape (subset we render). */
type EntriDnsRecord = {
  type: string;
  host: string;
  value: string;
  ttl: number;
  priority?: number | string;
};

/** Flatten Entri's `dnsRecords` (flat array OR keyed conditional sets) to a list. */
export function normalizeDnsRecords(config: EntriConfig | null | undefined): DnsRecordView[] {
  const raw = config?.dnsRecords;
  if (!raw) return [];

  const records: EntriDnsRecord[] = Array.isArray(raw)
    ? (raw as EntriDnsRecord[])
    : (Object.values(raw as Record<string, EntriDnsRecord[]>).flat() as EntriDnsRecord[]);

  return records
    .filter((r) => r && typeof r.type === 'string')
    .map((r) => ({
      type: r.type.toUpperCase(),
      host: r.host ?? '@',
      data: r.priority !== undefined && r.priority !== '' ? `${r.value} (Priority: ${r.priority})` : r.value,
      ttl: typeof r.ttl === 'number' ? r.ttl : Number(r.ttl) || 0,
    }));
}

/** Format a TTL (seconds) the way the design labels it: "1 Hour" / "600 seconds". */
export function formatTtl(ttl: number): string {
  if (ttl <= 0) return '—';
  if (ttl % 3600 === 0) {
    const hours = ttl / 3600;
    return hours === 1 ? '1 Hour' : `${hours} Hours`;
  }
  return `${ttl} seconds`;
}